import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileText, Loader2, ExternalLink, AlertCircle, Info } from "lucide-react";
import { useGetLastReportActividad } from "@/hooks/reportes/useGetLastReportActividad";
import { useGenerateSummaryActividad } from "@/hooks/reportes/useGenerateSummaryActividad";
import type { ActividadPrograma } from "@/interfaces/actividadPrograma.interface";
import { toast } from "sonner";

interface ModalReporteActividadProps {
  actividad: ActividadPrograma;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ModalReporteActividad({
  actividad,
  open,
  onOpenChange,
}: ModalReporteActividadProps) {
  const { data: lastReport, isLoading: isLoadingReport, error: reportError, refetch: refetchLastReport } = useGetLastReportActividad(open ? actividad.id : null);

  const { mutate: generateSummary, isPending: isGenerating } = useGenerateSummaryActividad({
    onSuccess: (data) => {
      // Refrescar el último reporte después de generar
      refetchLastReport();
      // Abrir PDF en nueva pestaña después de generar
      if (data.pdfUrl) {
        window.open(data.pdfUrl, "_blank", "noopener,noreferrer");
        toast.success("Reporte generado exitosamente");
      }
    },
  });

  const handleGenerate = () => {
    generateSummary(actividad.id);
  };

  const handleOpenReport = () => {
    if (lastReport?.url) {
      window.open(lastReport.url, "_blank", "noopener,noreferrer");
    }
  };

  const isLoading = isLoadingReport || isGenerating;
  const hasReport = !!lastReport?.url;
  const hasError = !!reportError && !isLoadingReport;

  const handleClose = (shouldClose: boolean) => {
    if (!isGenerating) {
      onOpenChange(shouldClose);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Reporte General - {actividad.nombre}
          </DialogTitle>
          <DialogDescription>
            Genera un reporte consolidado con todas las retroalimentaciones de esta actividad
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Estado de carga */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">
                {isGenerating
                  ? "Generando reporte... Este proceso puede tardar varios minutos."
                  : "Cargando información del reporte..."}
              </span>
            </div>
          )}

          {/* Error al cargar reporte */}
          {hasError && !isLoading && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                No se pudo obtener la información del reporte. Por favor, intenta nuevamente.
              </AlertDescription>
            </Alert>
          )}

          {/* Contenido cuando no está cargando */}
          {!isLoading && !hasError && (
            <>
              {hasReport ? (
                <div className="space-y-4">

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Reporte disponible</p>
                        <p className="text-xs text-muted-foreground">
                          Haz clic en el botón para abrir el PDF
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleOpenReport}
                      disabled={isGenerating}
                      variant="outline"
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Abrir Reporte
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Aún no se ha generado ningún reporte</AlertTitle>
                  <AlertDescription className="mt-2">
                    No hay reportes previos para esta actividad. Genera el primer reporte para comenzar.
                  </AlertDescription>
                </Alert>
              )}

              {/* Información adicional */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Información</AlertTitle>
                <AlertDescription className="mt-2">
                  Este proceso puede tardar varios minutos dependiendo del número de retroalimentaciones.
                  El reporte incluirá un análisis cuantitativo y cualitativo de todas las retroalimentaciones.
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={isGenerating}
          >
            Cerrar
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || isLoading}
            className="gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generando...
              </>
            ) : hasReport ? (
              <>
                <FileText className="h-4 w-4" />
                Actualizar Reporte
              </>
            ) : (
              <>
                <FileText className="h-4 w-4" />
                Generar Reporte
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
